import { Checkbox, FieldBlock, FilterCombo, FilterEditor, Select, emptyFilter, useToast } from '@orbind/ui'
import { MODOS_DOS_ITENS, type ConfigDaVisao, type ModoDaVisao, type SavedFilter, type Visao } from '@orbind/core'
import { useState } from 'react'
import type { QuickFilterCriteria } from './api.ts'
import { useConfirm } from './bits.tsx'
import type { FilterField } from '@orbind/ui'
import { WI_FILTER_FIELDS } from './filter-fields.ts'
import type { DefinicaoDeColuna } from './colunas-da-tabela.ts'
import { useMutacoesDeVisao, useVisoes } from '../visao/api.ts'
import { EditorDeVisao } from '../visao/EditorDeVisao.tsx'
import { opcoesDoCombo } from '../visao/visao.ts'
import { useSession } from '../../lib/session.tsx'

/** O que o editor precisa para escolher as colunas da visão (SPEC-20261103-1217). */
export type ColunasParaOFiltro = {
  catalogo: readonly DefinicaoDeColuna[]
  /** As ligadas na tela agora, sem o Título: ponto de partida das colunas próprias. */
  atuais: readonly string[]
}

/**
 * Visões da lista de Itens de Trabalho (SPEC-20261103-1418).
 *
 * Três fixos (Todos / Meus itens / Atrasados) e, depois deles, as VISÕES que a
 * pessoa vê: as dela, as compartilhadas com ela e as públicas. Até 2026-11-03
 * eram "filtros rápidos", só da pessoa; viraram visões com o mesmo id, então o
 * filtro escolhido continua escolhido.
 */

export type WiSelecao =
  | { tipo: 'builtin'; id: 'all' | 'mine' | 'overdue' }
  | { tipo: 'custom'; id: string; name: string; criteria: QuickFilterCriteria }

const FIXOS = [
  { id: 'all', label: 'Todos' },
  { id: 'mine', label: 'Meus itens' },
  { id: 'overdue', label: 'Atrasados' },
] as const

type Opcao = { value: string; label: string }

/** A seleção de uma visão: os critérios dela são o filtro da lista. */
export const selecaoDaVisao = (v: Visao): WiSelecao => ({
  tipo: 'custom',
  id: v.id,
  name: v.name,
  criteria: (v.config.filtro ?? {}) as QuickFilterCriteria,
})

/**
 * O combo da lista de Itens (SPEC-20261003-0927): o MESMO do quadro, um botão
 * que diz o que está valendo e abre a lista, com editar e remover ao lado de
 * cada nome. Os fixos não têm lápis nem lixeira: ninguém edita "Atrasados".
 * "Todos" é a opção que o próprio combo desenha, com activeId em null.
 */
export function FiltroDaLista({
  selecao,
  onSelecionar,
  statusOptions,
  typeOptions,
  priorityOptions,
  memberOptions,
  loadValueOptions,
  campos = WI_FILTER_FIELDS,
  colunas,
  modoAtual = 'lista',
  ordenacaoAtual,
}: {
  /** Colunas da lista, para a visão escolher as suas. Sem isto, o editor não mostra a seção. */
  colunas?: ColunasParaOFiltro
  selecao: WiSelecao
  onSelecionar: (s: WiSelecao) => void
  statusOptions: Opcao[]
  typeOptions: Opcao[]
  priorityOptions: Opcao[]
  memberOptions: Opcao[]
  loadValueOptions: (fieldKey: string, query: string) => { value: string; label: string; group?: string }[]
  /** Campos do construtor: os da barra de propriedades + os complementares (SPEC-20261008-1745-todos-os-campos-no-filtro). */
  campos?: readonly FilterField[]
  /** O tipo em uso: o de partida de uma visão nova. */
  modoAtual?: ModoDaVisao
  ordenacaoAtual?: ConfigDaVisao['ordenacao']
}) {
  const { data: visoes } = useVisoes(null)
  const { apagar } = useMutacoesDeVisao(null)
  const toast = useToast()
  const confirm = useConfirm()
  const user = useSession()
  const [editando, setEditando] = useState<Visao | 'nova' | null>(null)

  const lista = visoes ?? []
  const opcoes = [
    ...FIXOS.filter((f) => f.id !== 'all').map((f) => ({
      id: f.id,
      name: f.label,
      editavel: false,
      removivel: false,
    })),
    ...opcoesDoCombo(lista),
  ]

  const ativo =
    selecao.tipo === 'builtin' ? (selecao.id === 'all' ? null : selecao.id) : selecao.id

  const voltarParaTodos = () => onSelecionar({ tipo: 'builtin', id: 'all' })

  return (
    <>
      {confirm.element}
      <FilterCombo
        options={opcoes}
        activeId={ativo}
        allLabel="Todos"
        novoLabel="Nova visão"
        icone="fas fa-layer-group"
        rotuloDoItem="visão"
        onActivate={(id) => {
          if (id === null) return voltarParaTodos()
          const fixo = FIXOS.find((f) => f.id === id)
          if (fixo) return onSelecionar({ tipo: 'builtin', id: fixo.id })
          const visao = lista.find((v) => v.id === id)
          if (visao) onSelecionar(selecaoDaVisao(visao))
        }}
        onEdit={(opcao) => {
          const visao = lista.find((v) => v.id === opcao.id)
          if (visao) setEditando(visao)
        }}
        onRemove={(opcao) => {
          const visao = lista.find((v) => v.id === opcao.id)
          if (!visao) return
          confirm.ask(`A visão “${visao.name}” será apagada para todos que a veem.`, () => {
            apagar.mutate(visao.id, {
              onSuccess: () => {
                // a que estava em uso sai junto: deixar o id morto selecionado
                // esconderia itens sem nada explicar
                if (selecao.tipo === 'custom' && selecao.id === visao.id) voltarParaTodos()
                toast.push({ tone: 'success', message: 'Visão apagada.' })
              },
              onError: (e) =>
                toast.push({
                  tone: 'danger',
                  message: e instanceof Error ? e.message : 'Não foi possível apagar.',
                }),
            })
          })
        }}
        onNew={() => setEditando('nova')}
      />

      {editando ? (
        <EditorDeVisao
          quadroId={null}
          visao={editando === 'nova' ? null : editando}
          modos={MODOS_DOS_ITENS}
          modoAtual={modoAtual}
          ondeVale="a lista de Itens de Trabalho"
          membros={memberOptions}
          meuId={user.id}
          filtroInicial={selecao.tipo === 'custom' ? selecao.criteria : null}
          colunas={colunas}
          ordenacaoAtual={ordenacaoAtual}
          renderFiltro={(filtro, mudar) => (
            <CriteriosDoFiltroRapido
              valor={(filtro ?? {}) as QuickFilterCriteria}
              onChange={(c) => mudar(c as Record<string, unknown>)}
              statusOptions={statusOptions}
              typeOptions={typeOptions}
              priorityOptions={priorityOptions}
              memberOptions={memberOptions}
              loadValueOptions={loadValueOptions}
              campos={campos}
            />
          )}
          onClose={() => setEditando(null)}
          onApagada={(id) => {
            if (selecao.tipo === 'custom' && selecao.id === id) voltarParaTodos()
            setEditando(null)
          }}
          onSalva={(visao) => {
            onSelecionar(selecaoDaVisao(visao))
            setEditando(null)
          }}
        />
      ) : null}
    </>
  )
}

/**
 * Os critérios da lista de Itens: atalhos (só meus, atrasados), os quatro
 * seletores e as condições por campo. Só guarda o que foi marcado — critério
 * vazio significa "não restringe".
 */
function CriteriosDoFiltroRapido({
  valor,
  onChange,
  statusOptions,
  typeOptions,
  priorityOptions,
  memberOptions,
  loadValueOptions,
  campos,
}: {
  valor: QuickFilterCriteria
  onChange: (c: QuickFilterCriteria) => void
  statusOptions: Opcao[]
  typeOptions: Opcao[]
  priorityOptions: Opcao[]
  memberOptions: Opcao[]
  loadValueOptions: (fieldKey: string, query: string) => { value: string; label: string; group?: string }[]
  campos: readonly FilterField[]
}) {
  // o construtor precisa de um filtro inteiro, mesmo sem condição nenhuma
  const avancado: SavedFilter = valor.advanced ?? emptyFilter(WI_FILTER_FIELDS, '')
  const por = (mudanca: Partial<QuickFilterCriteria>) => {
    const novo: Record<string, unknown> = { ...valor, ...mudanca }
    for (const k of Object.keys(novo)) if (novo[k] === null || novo[k] === false || novo[k] === undefined) delete novo[k]
    onChange(novo as QuickFilterCriteria)
  }

  return (
    <div className="ob-stack">
      <div className="wi-qf__flags">
        <Checkbox label="Só meus" checked={valor.mine ?? false} onChange={(e) => por({ mine: e.target.checked })} />
        <Checkbox
          label="Atrasados"
          checked={valor.overdue ?? false}
          onChange={(e) => por({ overdue: e.target.checked })}
        />
      </div>

      <div className="wi-qf__grid4">
        <FieldBlock label="Status">
          {() => (
            <Select
              value={valor.statusId ?? null}
              options={statusOptions}
              placeholder="Qualquer status"
              onChange={(v) => por({ statusId: v || null })}
            />
          )}
        </FieldBlock>
        <FieldBlock label="Tipo">
          {() => (
            <Select
              value={valor.typeId ?? null}
              options={typeOptions}
              placeholder="Qualquer tipo"
              onChange={(v) => por({ typeId: v || null })}
            />
          )}
        </FieldBlock>
        <FieldBlock label="Prioridade">
          {() => (
            <Select
              value={valor.priorityId ?? null}
              options={priorityOptions}
              placeholder="Qualquer prioridade"
              onChange={(v) => por({ priorityId: v || null })}
            />
          )}
        </FieldBlock>
        <FieldBlock label="Responsável">
          {() => (
            <Select
              value={valor.assigneeId ?? null}
              options={memberOptions}
              placeholder="Qualquer responsável"
              onChange={(v) => por({ assigneeId: v || null })}
            />
          )}
        </FieldBlock>
      </div>

      {/* Construtor avancado por campo/operador/valor (pedido 2026-08-26). */}
      <div className="wi-qf__avancado">
        <span className="wi-qf__avancado-titulo">Condições por campo</span>
        <p className="wi-qf__avancado-dica">
          Some regras finas (contém, igual, diferente…) — valem junto com as opções acima.
        </p>
        <FilterEditor
          fields={campos}
          value={avancado}
          onChange={(f) => por({ advanced: f.criteria.length > 0 ? f : undefined })}
          loadValueOptions={loadValueOptions}
        />
      </div>
    </div>
  )
}
